import React, { useEffect, useState, Component } from 'react';
import { RocketIcon, ShieldCheckIcon, CpuIcon, BoxIcon, GitBranchIcon, TargetIcon, LayersIcon, SlidersIcon, RadioIcon, CodeIcon, ZapIcon, MenuIcon, XIcon, ChevronRightIcon } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────────
interface TocItem {
  id: string;
  label: string;
}
interface DocsLayoutProps {
  activePage: string;
  breadcrumb: string;
  tocItems: TocItem[];
  children: React.ReactNode;
}
// ─── Sidebar Data ────────────────────────────────────────────────
interface SidebarItem {
  name: string;
  icon: React.ElementType;
  href: string;
}
interface SidebarSection {
  title: string;
  items: SidebarItem[];
}
const sidebarSections: SidebarSection[] = [{
  title: 'Overview',
  items: [{
    name: 'Quickstart',
    icon: RocketIcon,
    href: '#/docs'
  }, {
    name: 'Principles',
    icon: ShieldCheckIcon,
    href: '#/docs/principles'
  }, {
    name: 'Models',
    icon: BoxIcon,
    href: '#/docs/models'
  }]
}, {
  title: 'Models & Routing',
  items: [{
    name: 'Model Fallbacks',
    icon: GitBranchIcon,
    href: '#'
  }, {
    name: 'Provider Selection',
    icon: TargetIcon,
    href: '#'
  }, {
    name: 'Bitstream Variants',
    icon: LayersIcon,
    href: '#'
  }]
}, {
  title: 'Features',
  items: [{
    name: 'Presets',
    icon: SlidersIcon,
    href: '#'
  }, {
    name: 'Streaming',
    icon: RadioIcon,
    href: '#'
  }, {
    name: 'Structured Outputs',
    icon: CodeIcon,
    href: '#'
  }, {
    name: 'Zero Downtime Deployment',
    icon: ZapIcon,
    href: '#'
  }]
}];
// ─── Sidebar Component ──────────────────────────────────────────
function DocsSidebar({
  activePage,
  onClose
}: {
  activePage: string;
  onClose?: () => void;
}) {
  return <nav className="py-6 pr-4" aria-label="Documentation navigation" data-id="element-31">
      {onClose && <div className="flex items-center justify-between mb-4 pl-4 lg:hidden" data-id="element-32">
          <span className="text-sm font-semibold text-white" data-id="element-33">Navigation</span>
          <button onClick={onClose} className="p-1 text-gate-text-muted hover:text-white transition-colors" aria-label="Close navigation" data-id="element-34">
            <XIcon className="w-5 h-5" data-id="element-35" />
          </button>
        </div>}
      {sidebarSections.map(section => <div key={section.title} className="mb-6" data-id="element-36">
          <h3 className="px-4 mb-2 text-xs font-semibold uppercase tracking-widest text-gate-text-muted" data-id="element-37">
            {section.title}
          </h3>
          <ul className="space-y-0.5" data-id="element-38">
            {section.items.map(item => {
          const isActive = item.name === activePage;
          return <li key={item.name} data-id="element-39">
                  <a href={item.href} onClick={() => onClose?.()} className={`flex items-center gap-2.5 px-4 py-2 text-sm rounded-lg transition-colors ${isActive ? 'bg-gate-teal/10 text-gate-teal font-medium border-l-2 border-gate-teal' : 'text-gate-text-secondary hover:text-white hover:bg-gate-surface/50'}`} data-id="element-40">
                    <item.icon className="w-4 h-4 flex-shrink-0" data-id="element-41" />
                    {item.name}
                  </a>
                </li>;
        })}
          </ul>
        </div>)}
    </nav>;
}
// ─── Table of Contents ──────────────────────────────────────────
function TableOfContents({
  items
}: {
  items: TocItem[];
}) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.find(e => e.isIntersecting);
      if (visible) setActiveId(visible.target.id);
    }, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1
    });
    items.forEach(({
      id
    }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);
  return <nav className="py-6 pl-4" aria-label="On this page" data-id="element-42">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-gate-text-muted" data-id="element-43">
        On this page
      </h3>
      <ul className="space-y-1 border-l border-gate-border" data-id="element-44">
        {items.map(({
        id,
        label
      }) => <li key={id} data-id="element-45">
            <a href={`#${id}`} className={`block pl-4 py-1 text-sm transition-colors border-l-2 -ml-px ${activeId === id ? 'border-gate-teal text-gate-teal font-medium' : 'border-transparent text-gate-text-muted hover:text-gate-text-secondary hover:border-gate-border'}`} data-id="element-46">
              {label}
            </a>
          </li>)}
      </ul>
    </nav>;
}
// ─── Layout Component ───────────────────────────────────────────
export function DocsLayout({
  activePage,
  breadcrumb,
  tocItems,
  children
}: DocsLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return <div className="min-h-screen bg-gate-dark" data-id="element-47">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden sticky top-16 z-30 bg-gate-dark/90 backdrop-blur-sm border-b border-gate-border/50 px-4 py-2" data-id="element-48">
        <button onClick={() => setSidebarOpen(true)} className="flex items-center gap-2 text-sm text-gate-text-secondary hover:text-white transition-colors" data-id="element-49">
          <MenuIcon className="w-4 h-4" data-id="element-50" />
          <span data-id="element-51">Documentation Menu</span>
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-52">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} data-id="element-53" />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-gate-dark border-r border-gate-border overflow-y-auto" data-id="element-54">
            <DocsSidebar activePage={activePage} onClose={() => setSidebarOpen(false)} data-id="element-55" />
          </div>
        </div>}

      <div className="max-w-[90rem] mx-auto flex" data-id="element-56">
        {/* Left Sidebar — desktop */}
        <aside className="hidden lg:block w-64 flex-shrink-0 border-r border-gate-border/50 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto" data-id="element-57">
          <DocsSidebar activePage={activePage} data-id="element-58" />
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-6 sm:px-8 lg:px-12 py-10 max-w-3xl mx-auto lg:mx-0" data-id="element-59">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm text-gate-text-muted mb-8" data-id="element-60">
            <span data-id="element-61">Overview</span>
            <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-62" />
            <span className="text-gate-text-secondary" data-id="element-63">{breadcrumb}</span>
          </div>

          {children}
        </main>

        {/* Right Sidebar — TOC */}
        <aside className="hidden xl:block w-56 flex-shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto" data-id="element-64">
          <TableOfContents items={tocItems} data-id="element-65" />
        </aside>
      </div>
    </div>;
}